<template>
  <div class="flex flex-row gap-2 print:hidden">
    <div v-if="searchValue || searchValue === ''">
      <BaseSearchInput v-model="localSearchValue" size="small" />
    </div>
    <div v-if="!selectionActive && $slots['display-options-content']">
      <DisplayOptionsComponent>
        <template #display-options-content="{ close }">
          <slot name="display-options-content" :close="close" />
        </template>
      </DisplayOptionsComponent>
    </div>
    <div v-if="$slots['filter-content']" class="">
      <FilterContainerComponent
        ref="filterContainer"
        :count="countFilter ?? 0"
        :is-dirty="isDirty"
        @clear="$emit('clear-filters')"
      >
        <template #filter-content="{ close }">
          <slot name="filter-content" :close="close" />
        </template>
      </FilterContainerComponent>
    </div>
    <div v-if="$slots['cta']">
      <slot name="cta" :close="close" />
    </div>
    <div v-if="!selectionActive && $slots['other-cta']">
      <slot name="other-cta" />
    </div>
    <ConfirmModal ref="checkDepositIndex" />
    <div class="flex flex-row gap-2">
      <BaseDropDownMenu :options="actionMenuOptions" />
      <div>
        <BaseButton
          v-if="
            localRequireSaveDashboard &&
            $can('manage_dashboards') &&
            !['statistics'].includes(dashboard?.type)
          "
          icon="paper-plane"
          color="success"
          size="small"
          @click.prevent="() => saveDashboard()"
        />
      </div>
    </div>
  </div>
  <DashboardModal
    :open="modalOpened"
    :dashboard-id="dashboard.id"
    :action="modalAction"
    :dashboard-template="dashboard"
    @close="modalOpened = false"
    @dashboard-changed="dashboardChanged"
  />
</template>

<script>
import FilterContainerComponent from './FilterContainerComponent.vue'
import DisplayOptionsComponent from '@c/Dashboard/Components/DisplayOptionsComponent.vue'
import DashboardModal from '@c/Dashboard/Components/DashboardModal.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import DashboardTypes from '@/assets/enums/dashboard/DashboardTypes'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

export default {
  name: 'DashboardPanelControl',
  components: {
    BaseButton,
    ConfirmModal,
    BaseSearchInput,
    BaseDropDownMenu,
    DashboardModal,
    DisplayOptionsComponent,
    FilterContainerComponent
  },
  props: {
    dashboard: {
      type: Object,
      required: true
    },
    countFilter: {
      type: Number,
      required: false,
      default: 0
    },
    isDirty: {
      type: Boolean,
      required: false,
      default: false
    },
    searchValue: {
      type: String,
      required: false,
      default: null
    },
    needSaveLocked: {
      type: Boolean,
      required: true,
      default: false
    },
    selectionActive: {
      type: Boolean,
      required: false,
      default: false
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:require-save-dashboard',
    'update:dashboard',
    'dashboard-changed',
    'update:searchValue',
    'exportFile',
    'clear-filters'
  ],
  data() {
    return {
      modalOpened: false,
      modalAction: null,
      openConfirmModal: false,
      actionMenuOptions: [],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth'
    }),
    localSearchValue: {
      get() {
        return this.searchValue
      },
      set(value) {
        this.$emit('update:searchValue', value)
      }
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    }
  },
  watch: {
    searchValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('update:searchValue', newValue)
      }
    },
    'dashboard.sort': {
      handler(newValue, oldValue) {
        if (
          [
            DashboardTypes.PLANNING,
            DashboardTypes.AGENDA,
            DashboardTypes.TASK
          ].includes(this.dashboard.type) &&
          !this.needSaveLocked
        ) {
          this.localRequireSaveDashboard = true
        }
      },
      deep: true
    }
  },
  mounted() {
    this.actionMenuOptions = [
      [
        {
          label: this.$t('button.save'),
          icon: 'paper-plane',
          gate:
            this.auth.roles.includes('super-admin') ||
            (this.$can('manage_dashboards') &&
              ![DashboardTypes.STATISTICS].includes(this.dashboard?.type) &&
              !(
                this.auth?.id !== this.dashboard.creator_id &&
                this.dashboard.locked
              )),

          action: () => this.saveDashboard()
        },
        {
          label: this.$t('button.edit'),
          icon: 'edit',
          gate:
            this.auth.roles.includes('super-admin') ||
            (this.$can('manage_dashboards') &&
              !(
                this.auth?.id !== this.dashboard.creator_id &&
                this.dashboard.locked
              )),
          action: () => this.editDashboard()
        },
        {
          label: this.$t('button.duplicate'),
          icon: 'clone',
          gate:
            this.auth.roles.includes('super-admin') ||
            (this.$can('create_dashboards') &&
              ![DashboardTypes.STATISTICS].includes(this.dashboard?.type) &&
              !(
                this.auth?.id !== this.dashboard.creator_id &&
                this.dashboard.locked
              )),
          action: () => this.duplicateDashboard()
        },
        {
          label: this.$t('global.export_list'),
          icon: 'file-export',
          gate:
            this.$can('manage_dashboards') &&
            [
              DashboardTypes.INVOICE,
              DashboardTypes.QUOTE,
              DashboardTypes.PURCHASE_ORDER,
              DashboardTypes.CREDIT_NOTE,
              DashboardTypes.PAYMENT,
              DashboardTypes.INVOICE_ITEM_THIRD_PARTY_COST
            ].includes(this.dashboard?.type),
          action: this.handleExportFile
        },
        // {
        //   label: this.$t('button.export'),
        //   icon: 'file-export',
        //   gate: [DashboardTypes.STATISTICS].includes(this.dashboard?.type),
        //   action: this.excelExport
        // },
        {
          label: this.$t('button.update_m', {
            var: this.$t('enums.config.permissions.permissions_group.dashboard')
          }),
          icon: 'rotate',
          gate:
            this.auth.roles.includes('super-admin') ||
            (this.$can('manage_dashboards') &&
              ![DashboardTypes.STATISTICS].includes(this.dashboard?.type) &&
              !(
                this.auth?.id !== this.dashboard.creator_id &&
                this.dashboard.locked
              )),
          action: () => this.setFieldColumnDashboard()
        }
      ],
      [
        {
          label: this.$t('button.unsubscribe'),
          icon: 'trash',
          gate: true,
          bg_color: 'bg-red-500',
          txt_color: 'text-white',
          action: () => this.deleteDashboard()
        }
      ]
    ]
  },
  methods: {
    ...mapActions({
      processUpdateTemplateDashboardByIdAction:
        'dashboard/updateTemplateDashboardById',
      processUnsubscribeToTemplateDashboardAction:
        'dashboard/unsubscribeToTemplateDashboard',
      processSetfieldToColumnTemplateDashboardByIdAction:
        'dashboard/setFieldToColumnDashboard',
      processRefreshSpecificInitialMetadataModelsAction:
        'metadata/refreshSpecificInitialMetadataModels',
      processFetchStatisticsForExportAction:
        'statistics/fetchStatisticsForExport'
    }),
    openFilters() {
      this.$refs.filterContainer?.openPopover()
    },
    dashboardChanged(dashboard_template_id, go_to_dashboard) {
      if (this.dashboard.id === dashboard_template_id) {
        this.$emit('dashboard-changed', dashboard_template_id)
      } else if (go_to_dashboard) {
        this.goToNewDashboard(dashboard_template_id)
      }
    },
    goToNewDashboard(template_dashboard_id) {
      let route_template_dashboard = ''

      const dashboardType = this.dashboard.type || ''

      switch (dashboardType) {
        // Go dossier view
        case DashboardTypes.DASHBOARD:
          route_template_dashboard = 'dashboard_dossier_page_route'
          break
        // Go agenda view
        case DashboardTypes.AGENDA:
          route_template_dashboard = 'agenda_page_route_id'
          break
        // Go planning view
        case DashboardTypes.PLANNING:
          route_template_dashboard = 'planning_page_route_id'
          break
        // Go supplier order view
        case DashboardTypes.SUPPLIER_ORDER:
          route_template_dashboard = 'supplier_order_page_route_id'
          break
        // Go analytics view
        case DashboardTypes.STATISTICS:
          route_template_dashboard = 'dashboard_statistics_route_id'
          break
        // Go invoice default view
        case DashboardTypes.INVOICE:
          route_template_dashboard = 'invoice_index_route_id'
          break
        // Go invoice purchase order view
        case DashboardTypes.PURCHASE_ORDER:
          route_template_dashboard = 'purchase_order_index_route_id'
          break
        // Go invoice quote view
        case DashboardTypes.QUOTE:
          route_template_dashboard = 'quote_index_route_id'
          break
        // Go invoice credit note view
        case DashboardTypes.CREDIT_NOTE:
          route_template_dashboard = 'credit_note_index_route_id'
          break
        case DashboardTypes.PAYMENT:
          route_template_dashboard = 'payment_index_route_id'
          break
      }

      if (route_template_dashboard) {
        this.$router.push({
          name: route_template_dashboard,
          params: {
            id: template_dashboard_id
          }
        })
      }
    },
    editDashboard() {
      this.modalAction = 'edit'
      this.modalOpened = true
    },
    duplicateDashboard() {
      this.modalAction = 'duplicate'
      this.modalOpened = true
    },
    handleExportFile() {
      this.isLoading = true
      this.$emit('exportFile')
    },
    excelExport() {
      this.isLoading = true
      this.processFetchStatisticsForExportAction({
        id: this.dashboard.id,
        successCallback: () => {
          this.isLoading = false
          // this.$h.toastify(this.$t('toasts.import_list_with_success'), {
          //   className: 'toastify-content success'
          // })
        },
        errorCallback: () => {
          this.$h.toastify(
            this.$t('global.error_when_import_file'),
            { className: 'toastify-content warning' },
            (this.isErrorModalOpen = true)
          )
        }
      })
    },
    saveDashboard(callback = null) {
      this.processUpdateTemplateDashboardByIdAction({
        id: this.dashboard.id,
        data: this.dashboard
      }).then((response) => {
        this.$emit('update:dashboard', response.data.template_dashboard)
        this.$h.toastSuccess(this.$t('global.dashboard_saved'))
        this.$nextTick(() => {
          this.localRequireSaveDashboard = false
        })
        if (typeof callback === 'function') {
          callback()
        }
      }).catch(() => {
        this.$h.toastError(this.$t('toasts.something_went_wrong'))
      })
    },
    deleteDashboard() {
      this.processUnsubscribeToTemplateDashboardAction({
        template_dashboard_id: this.dashboard.id
      }).then((_response) => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        })
        this.$h.toastSuccess(this.$t('toasts.dashboard_unsubscribed'))
      })
    },
    setFieldColumnDashboard() {
      this.processSetfieldToColumnTemplateDashboardByIdAction({
        id: this.dashboard.id,
        data: this.dashboard
      }).then((response) => {
        this.$emit('update:dashboard', response.data.template_dashboard)
        this.$h.toastSuccess(this.$t('global.dashboard_saved'))
        this.$nextTick(() => {
          this.localRequireSaveDashboard = false
        })
      })
    }
  }
}
</script>

<style scoped></style>
